import { SingleSelectDropdown, Spinner } from "@carbon6/c6-frontend-design";
import { useCallback, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  getObservabilityLatency,
  getObservabilityQueries,
  getObservabilityTables,
  hasCyclesInWindow,
  HOTSPOT_LIMIT,
  LATENCY_OPERATION_LIMIT_PER_SURFACE,
  type LatencyOperationMetricsResp,
  type MetricWindow,
} from "@/api";
import { hatcheryDropdownModes } from "@/app/theme";
import { GnbMenu } from "@/components/base";
import { FilterBar } from "@/components/filter";
import { EmptyState, noticeChipClassName } from "@/shared/ui/emptyState/public";
import {
  inlineLoadingLineClassName,
  inlineSpinnerClassName,
} from "@/shared/ui/loadingState/public";
import { formatNumber } from "@/utils";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
import { ObservabilityDatabasePanel } from "./ObservabilityDatabasePanel";
import { ObservabilityTablePanel } from "./ObservabilityTablePanel";
import { ObservabilityRuntimePanel } from "./ObservabilityRuntimePanel";
import { createLatencyColumns } from "./latencyColumns";
import { createLatencyRows } from "./LatencyTableRow";
import { describeCollectionHealth } from "./observabilityModel";
import {
  defaultObservabilityTab,
  isObservabilityTableTab,
  normalizeObservabilityTab,
  OBSERVABILITY_TAB_PARAM,
  observabilityTabItems,
  type ObservabilityTab,
} from "./observabilityTabs";
import {
  metricWindowOptions,
  normalizeMetricWindow,
  OBSERVABILITY_WINDOW_PARAM,
} from "./observabilityWindow";
import { createQueryHotspotColumns } from "./queryHotspotColumns";
import { createQueryHotspotRows } from "./QueryHotspotTableRow";
import { createTableHotspotColumns } from "./tableHotspotColumns";
import { createTableHotspotRows } from "./TableHotspotTableRow";
import { useCollectionSummary, type CollectionController } from "./useObservabilityMetric";
import styles from "./ObservabilityPage.module.css";

/**
 * 지연 탭이 쓰는 fetcher 다. 모듈 상수여야 한다.
 *
 * `useObservabilityMetric` 은 fetcher 가 바뀌면 탭이 바뀐 것으로 보고 직전 데이터를 버린다
 * (ZERG-485). 렌더 안에서 화살표 함수로 만들면 매 렌더마다 새 함수가 되어 표가 계속 비워진다.
 */
const fetchLatencyOperations = (window: MetricWindow): Promise<LatencyOperationMetricsResp[]> =>
  getObservabilityLatency(window).then((metrics) => metrics.operations);

/**
 * Observability 화면이다. 수집기가 5분마다 저장한 PostgreSQL·API pod 스냅샷을 창 단위로 본다.
 *
 * 이 화면이 **다루지 않는 것**:
 * - 원시 요청/트래픽 로그
 * - MCP tool flow
 * - 감사 로그
 * 그 telemetry 는 registry DB 에 오지 않는다. Cloud Logging 이 소유한다(backend `docs/rules.md`
 * §11). operation 단위 지연 **집계**는 다룬다. 행 수는 도메인 크기가 아니라 `시간 × top-K` 로
 * 늘어나 상한이 있다. 그래서 §11 이 금지하는 per-item row 가 아니다. 워커 하트비트는 Workers
 * 화면이 소유한다. 잡 큐는 아직 없는 별도 화면이 소유한다.
 *
 * 창은 **페이지 전역 축 하나**다. 이 화면의 목적은 같은 사고를 DB·쿼리·런타임·지연으로 대조하는
 * 것이다. 탭마다 다른 창을 두면 이 목적을 이룰 수 없다.
 */
export function ObservabilityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeObservabilityTab(searchParams.get(OBSERVABILITY_TAB_PARAM));
  const window = normalizeMetricWindow(searchParams.get(OBSERVABILITY_WINDOW_PARAM));

  // 기본값이면 키를 지운다. 그래서 불필요한 키가 없는 URL 이 canonical 이 된다.
  const writeParam = useCallback(
    (key: string, next: string, fallback: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === fallback) {
          params.delete(key);
        } else {
          params.set(key, next);
        }
        return params;
      });
    },
    [setSearchParams],
  );

  const collection = useCollectionSummary(window);

  return (
    <ObservabilityPageView
      pageTitle="Observability"
      currentMenu={GnbMenu.AdminObservability}
      requiredRoles={["admin"]}
      // 표가 본문 전체인 탭은 셸이 스크롤하지 않는다. 표가 자기 안에서 스크롤한다(rules.md §14).
      scrollable={!isObservabilityTableTab(tab)}
      // 창 컨트롤 블록이 이미 헤더와 본문의 경계를 만든다. 그래서 tabpanel 상단 여백을 더하지 않는다.
      bodyFlushTop
      window={window}
      collection={collection}
      onWindowChange={(next) =>
        writeParam(OBSERVABILITY_WINDOW_PARAM, next, normalizeMetricWindow(null))
      }
      tabs={{
        items: observabilityTabItems,
        value: tab,
        onValueChange: (next) => writeParam(OBSERVABILITY_TAB_PARAM, next, defaultObservabilityTab),
        ariaLabel: "Observability 탭",
      }}
    />
  );
}

type ObservabilityPageViewProps = PageBaseProps & {
  window: MetricWindow;
  collection: CollectionController;
  onWindowChange: (next: MetricWindow) => void;
};

class ObservabilityPageView extends PageBase<ObservabilityPageViewProps> {
  /**
   * 창 선택과 수집 건강 readout 이다. 모든 탭 위에 항상 표시한다.
   *
   * 수집 건강을 별도 탭으로 분리하지 않는 이유: 이 정보는 "지금 보는 지표를 믿어도 되는가" 에
   * 먼저 답한다. 탭 뒤에 두면 사용자가 보지 않는다. 그러면 수집이 멈춰 비어 있던 구간을
   * "값이 0 이었다" 로 읽는다.
   */
  protected override renderBelowTabs(): ReactNode {
    const { window, collection, onWindowChange } = this.props;
    return (
      <FilterBar chips={[]} onClearAll={() => {}} ariaLabel="Observability 조회 구간">
        <div className={styles.headerRow}>
          <SingleSelectDropdown
            mode={hatcheryDropdownModes.filter}
            label="창"
            value={window}
            options={[...metricWindowOptions]}
            onValueChange={(next) => onWindowChange(next as MetricWindow)}
          />
          {renderCollectionReadout(collection)}
        </div>
      </FilterBar>
    );
  }

  protected override renderContent(context: PageBaseContext): ReactNode {
    const { window, collection } = this.props;

    // 수집이 비어 있으면 탭 본문을 그리지 않는다. 탭마다 빈 차트를 그리면 그 차트가
    // "값이 0 이었다" 로 읽힌다. 빈칸의 **뜻**은 컨트롤러가 보존 구간을 조회해 구분한다.
    if (collection.state.status === "success" && !hasCyclesInWindow(collection.state.data)) {
      return <EmptyState size="lg">{emptyCollectionText(collection)}</EmptyState>;
    }

    const tab = (context.currentTab ?? defaultObservabilityTab) as ObservabilityTab;
    switch (tab) {
      case "tables":
        return (
          <ObservabilityTablePanel
            window={window}
            fetcher={getObservabilityTables}
            toRows={createTableHotspotRows}
            columns={createTableHotspotColumns()}
            note={`크기 상위 ${formatNumber(HOTSPOT_LIMIT)}개 테이블 · 블록 접근 순 (열 머리글로 정렬)`}
            loadingText="테이블 지표를 불러오고 있습니다."
            emptyMessage="이 구간에 표본이 없습니다."
            ariaLabel="테이블 hotspot 테이블"
            minWidth="2030px"
          />
        );
      case "queries":
        return (
          <ObservabilityTablePanel
            window={window}
            fetcher={getObservabilityQueries}
            toRows={createQueryHotspotRows}
            columns={createQueryHotspotColumns()}
            note={`이 구간에 쓴 총 시간 상위 ${formatNumber(HOTSPOT_LIMIT)}건 (서버 고정 정렬) · SQL 원문은 수집하지 않습니다`}
            loadingText="쿼리 지표를 불러오고 있습니다."
            emptyMessage="이 구간에 표본이 없습니다."
            ariaLabel="쿼리 hotspot 테이블"
            minWidth="1080px"
          />
        );
      case "latency":
        return (
          <ObservabilityTablePanel
            window={window}
            fetcher={fetchLatencyOperations}
            toRows={createLatencyRows}
            columns={createLatencyColumns()}
            note={`surface 별 호출 수 상위 ${formatNumber(LATENCY_OPERATION_LIMIT_PER_SURFACE)}개 · 표는 실측 최대 순으로 다시 세웁니다 (열 머리글로 정렬)`}
            loadingText="지연 지표를 불러오고 있습니다."
            emptyMessage="이 구간에 표본이 없습니다."
            ariaLabel="지연 테이블"
            minWidth="1410px"
          />
        );
      case "runtime":
        return <ObservabilityRuntimePanel window={window} />;
      case "database":
        return <ObservabilityDatabasePanel window={window} />;
    }
  }
}

function renderCollectionReadout(collection: CollectionController): ReactNode {
  // 머리 줄 안의 상태다. 그래서 면을 깔지 않는다. 실패는 주황 칩이고 대기는 글자 옆 스피너다.
  // 수집 상태를 못 읽어도 탭 본문은 그려진다. 그래서 실패는 오류가 아니라 경고 강도다.
  if (collection.state.status === "error") {
    return <span className={noticeChipClassName}>수집 상태를 읽지 못했습니다.</span>;
  }
  if (collection.state.status !== "success") {
    return (
      <span className={`${styles.collectionReadout} ${inlineLoadingLineClassName}`}>
        <Spinner size="sm" className={inlineSpinnerClassName} />
        수집 상태 확인 중…
      </span>
    );
  }
  if (!hasCyclesInWindow(collection.state.data)) {
    return <span className={styles.collectionReadout}>{emptyCollectionText(collection)}</span>;
  }
  const view = describeCollectionHealth(collection.state.data);
  const className = view.isDegraded
    ? `${styles.collectionReadout} ${styles.collectionDegraded}`
    : styles.collectionReadout;
  return <span className={className}>수집 {view.text}</span>;
}

/**
 * 사이클이 없는 창의 안내 문구다.
 *
 * 경우는 세 가지다:
 * - 수집기가 실행되지 않는다. 사용자는 배포를 확인한다.
 * - 수집기는 실행되지만 이 창에만 사이클이 없다. 사용자는 창을 넓힌다.
 * - 보존 구간 조회가 실패해 판정할 수 없다. 원인을 단정하지 않고 관측한 사실만 적는다.
 */
function emptyCollectionText(collection: CollectionController): string {
  switch (collection.emptiness) {
    case "collectorIdle":
      return "수집기가 아직 실행되지 않았습니다. 보존 구간(30일) 안에 남은 스냅샷이 없습니다.";
    case "windowEmpty":
      return "이 구간에 수집 사이클이 없습니다. 창을 넓히면 이전 스냅샷을 볼 수 있습니다.";
    case "unknown":
      return "이 구간에 수집 사이클이 없습니다.";
  }
}
